import { PlusOutlined } from "@ant-design/icons";
import * as Sentry from "@sentry/browser";
import { Button, Table, message } from "antd";
import debounce from "lodash.debounce";
import React from "react";
import { RouteComponentProps } from "react-router-dom";
import { NavLink } from "react-router-dom";
import AdvancedSearchModal, {
  AdvancedSearchReturn,
  IField,
} from "../components/AdvancedSearchModal";
import { PageToolbar } from "../components/PageToolbar";
import Card from "../components/Card";
import EntitledSection from "../components/EntitledSection";
import ModalCreator from "../components/ModalCreator";
import entitlements from "../config/entitlements";
import ExportData from "../components/ExportData";
import { Provider } from "../helpers/shared-interfaces";
import {
  request,
} from "../helpers/utilities";
import { GET_PROVIDERS } from "../gql/loans";
import { NewProvider } from "../components/NewProvider";
import { DEBOUNCE_TIME } from "../helpers/constants";


interface State {
  isFiltering: boolean;
  providers: Provider[];
  page: number;
  providerCount: number;
  filteredProviders: Provider[];
  filterBy: string;
  isLoading: boolean;
  showDialog: boolean;
  showAdvancedSearchModal: boolean;
  selectedFieldsToDisplay: IField[];
  removedField?: IField;
  showNewProviderModal: boolean;
}

const columns = [
  {
    title: "Provider Name",
    dataIndex: "name",
    key: "name",
    width: "40%",
    render(text: string, record: Provider): JSX.Element {
      return (
        <NavLink
          to={`/providers/${record.id}`}
          data-cy="test-provider-details-link"
        >
          {text}
        </NavLink>
      );
    },
  },
  {
    title: "Active Products",
    dataIndex: "activeProviderProducts",
    key: "activeProviderProducts",
    width: "30%",
    render(activeProviderProducts: any[], record: Provider): JSX.Element {
      return <span>{activeProviderProducts?.length || 0}</span>;
    },
  },
  {
    title: "Created Date",
    dataIndex: "createdAt",
    key: "createdAt",
    width: "30%",
    render(text: string): JSX.Element {
      return <span>{new Date(text).toLocaleDateString()}</span>;
    },
  },
];

export function ProviderTableFactory(
  data: Provider[],
  isLoading: boolean,
  count: number,
  page: number,
  nextPage: (page: number) => void,
): JSX.Element {
  const tempData: Provider[] = data.map((provider: Provider): Provider => {
    return { ...provider, id: provider.id };
  });
  return (
    <Table
      loading={isLoading}
      scroll={{ x: 500 }}
      columns={columns}
      dataSource={tempData}
      pagination={{
        current: page,
        defaultPageSize: 25,
        total: count,
        onChange: nextPage,
      }}
    />
  );
}

export default class Providers extends React.Component<
  RouteComponentProps,
  State
> {
  constructor(props: RouteComponentProps) {
    super(props);
    this.state = {
      showDialog: false,
      filterBy: "",
      isLoading: true,
      providers: [],
      page: 1,
      providerCount: 0,
      filteredProviders: [],
      isFiltering: false,
      showAdvancedSearchModal: false,
      selectedFieldsToDisplay: [],
      showNewProviderModal: false,
    };

    this.getAllProviders = this.getAllProviders.bind(this);
    this.filterProviders = this.filterProviders.bind(this);
    this.nextPage = this.nextPage.bind(this);
    this.toggleCreateProviderModal = this.toggleCreateProviderModal.bind(this);
  }

  async filterProviders(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    e.persist();
    this.setState({ filterBy: e.target.value, isLoading: true });
    this.triggerDelayedSearch();
  }

  triggerDelayedSearch = debounce(async (): Promise<void> => {
    this.getAllProviders();
  }, DEBOUNCE_TIME);

  getAllProviders(): void {
    const { page } = this.state;
    this.setState({ isLoading: true });
    request({
      query: GET_PROVIDERS,
      variables: {
        page: page.toString(),
        perPage: "25",
      },
    })
      .then(({ data: { data } }: any): void => {
        const providers = data.getProviders || [];
        this.setState({
          providers: providers,
          providerCount: providers.length,
        });
      })
      .catch((error) => {
        message.error("Failed to load providers");
        Sentry.captureException(error);
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  }

  toggleAdvancedSearchModel = (): void => {
    const prevState = this.state.showAdvancedSearchModal;
    this.setState({ showAdvancedSearchModal: !prevState });
  };

  toggleCreateProviderModal(): void {
    this.setState((prevState) => ({
      showNewProviderModal: !prevState.showNewProviderModal,
    }));
  }

  handleProviderCreated = (provider: Provider): void => {
    // Optionally refresh the providers list
    // this.getAllProviders();
    this.toggleCreateProviderModal();
  };

  handleOnAdvancedSearchData = (
    data: AdvancedSearchReturn,
    fields: IField[],
  ) => {
    this.setState({
      providers: data.results as Provider[],
      selectedFieldsToDisplay: fields,
      page: 1,
      providerCount: data.count,
    });
  };

  handleOnAdvancedSearchError = (error: string) => {
    message.error("Failed to execute query");
    Sentry.captureException(error);
  };

  handleOnFieldRemove = (field: IField) => {
    const fields = this.state.selectedFieldsToDisplay.filter(
      (f) => f.name !== field.name,
    );
    this.setState({
      removedField: field,
      selectedFieldsToDisplay: fields,
    });

    if (fields.length === 0) this.getAllProviders();
  };

  componentDidMount(): void {
    this.setState({ isLoading: true }, (): void => {
      this.getAllProviders();
    });
  }

  nextPage(page: number) {
    this.setState({ isLoading: true, page }, (): void => {
      this.getAllProviders();
    });
  }

  render(): JSX.Element | null {
    const {
      providers,
      isFiltering,
      filteredProviders,
      isLoading,
      providerCount,
      page,
      showNewProviderModal,
      selectedFieldsToDisplay,
      removedField,
    } = this.state;
    return (
      <>
        <AdvancedSearchModal
          toggleModal={this.toggleAdvancedSearchModel}
          showModal={this.state.showAdvancedSearchModal}
          model="provider"
          onError={this.handleOnAdvancedSearchError}
          onData={this.handleOnAdvancedSearchData}
          triggerField={removedField?.name}
          page={this.state.page}
          pageSize={25}
        />
          <Card>
            <div className="deparments-table-container">
              <PageToolbar modelName="providers"
                searchPlaceholder="Filter by provider name..."
                searchValue={this.state.filterBy}
                onSearchChange={this.filterProviders}
                totalCount={providerCount}
                currentDataCount={providers.length}
                page={page}
                pageSize={25}
                advancedSearch={{
                  onOpen: this.toggleAdvancedSearchModel,
                  activeFields: selectedFieldsToDisplay,
                  onFieldRemove: this.handleOnFieldRemove,
                }}
                primaryAction={
                  <EntitledSection entitlement={entitlements.LoanProviders?.create}>
                    <Button
                      className="base-button"
                      size="large"
                      onClick={this.toggleCreateProviderModal}
                      data-cy="test-new-provider-button"
                    >
                      <PlusOutlined />
                      Create Provider
                    </Button>
                  </EntitledSection>
                }
                actions={
                  <ExportData
                    model={"provider"}
                    dataArray={this.state.providers}
                    size={"large"}
                  />
                }
              />
              {ProviderTableFactory(
                isFiltering ? filteredProviders : providers,
                isLoading,
                providerCount,
                page,
                this.nextPage,
              )}
              {showNewProviderModal &&
                ModalCreator({
                  title: "Create Provider",
                  showModal: showNewProviderModal,
                  showFooter: false,
                  onOkay: this.toggleCreateProviderModal,
                  toggleModal: this.toggleCreateProviderModal,
                  inscription: (
                    <NewProvider
                      onSuccess={this.handleProviderCreated}
                      onCancel={this.toggleCreateProviderModal}
                    />
                  ),
                })}
            </div>
          </Card>
      </>
    );
  }
}
